import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileCheck, CheckCircle2, X, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageLoading } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS_CLR = {
  PENDING: "bg-amber-100 text-amber-700",
  VERIFIED: "bg-emerald-100 text-emerald-700",
  REJECTED: "bg-red-100 text-red-700",
};
const TYPE_LABEL = { passport: "Reisepass", residence_permit: "Aufenthaltstitel" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function DocumentVerifications() {
  const { user } = useAuth();
  const isAdmin = ["COMPANY_OWNER", "COMPANY_ADMIN", "MANAGER"].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("PENDING");
  const [busy, setBusy] = useState("");
  const [exp, setExp] = useState({});

  const load = useCallback(() => {
    const qs = status ? `?status=${status}` : "";
    api.get(`/employer/document-verifications${qs}`)
      .then((r) => { setRows(r.data.documents || []); const m = {}; (r.data.documents || []).forEach((d) => { m[d.id] = d.ai_expiry || ""; }); setExp(m); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const verify = async (id) => {
    setBusy(id);
    try { await api.post(`/employer/document-verifications/${id}/verify`, { expiry_date: exp[id] || undefined }); toast.success("Bestätigt — Ablaufdatum übernommen"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy("");
  };
  const reject = async (id) => {
    const review_note = window.prompt("Grund der Ablehnung (optional):") ?? undefined;
    setBusy(id);
    try { await api.post(`/employer/document-verifications/${id}/reject`, { review_note }); toast.success("Abgelehnt"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy("");
  };

  if (!rows) return <PageLoading />;

  return (
    <div data-testid="document-verifications">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Link to="/app/employer/employees" className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="docv-back"><ArrowLeft className="h-4 w-4" /> Zurück</Link>
        <h1 className="ml-2 flex items-center gap-2 font-heading text-2xl font-extrabold text-navy"><FileCheck className="h-6 w-6 text-cyan-dark" /> Dokument-Prüfung</h1>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="ml-auto rounded-lg border border-slate-200 px-2 py-2 text-sm" data-testid="docv-status-filter">
          <option value="PENDING">Offen</option>
          <option value="VERIFIED">Bestätigt</option>
          <option value="REJECTED">Abgelehnt</option>
          <option value="">Alle</option>
        </select>
      </div>
      <p className="mb-6 text-sm text-slate-500">Von Mitarbeitern über die App hochgeladene Dokumente. KI liest das Ablaufdatum aus — nach Ihrer Bestätigung wird es in die Compliance-Fristen übernommen (Vier-Augen-Prinzip).</p>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="docv-table">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>{["Mitarbeiter", "Typ", "Dokument", "Ablaufdatum", "Status"].map((c) => <th key={c} className="px-4 py-3">{c}</th>)}<th /></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} className="p-10 text-center text-slate-400" data-testid="docv-empty">Keine Dokumente.</td></tr> :
              rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 align-top hover:bg-slate-50" data-testid={`docv-row-${r.id}`}>
                  <td className="px-4 py-3"><div className="font-medium text-navy">{r.worker_name}</div><div className="text-xs text-slate-400">{r.worker_id}</div></td>
                  <td className="px-4 py-3 text-slate-600">{TYPE_LABEL[r.type] || r.type}</td>
                  <td className="px-4 py-3">
                    <a href={`${BACKEND}/api/documents/${r.id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cyan-dark hover:underline" data-testid={`docv-view-${r.id}`}>Ansehen <ExternalLink className="h-3 w-3" /></a>
                  </td>
                  <td className="px-4 py-3">
                    {r.verification_status === "PENDING" && isAdmin ? (
                      <Input type="date" value={exp[r.id] || ""} onChange={(ev) => setExp((s) => ({ ...s, [r.id]: ev.target.value }))} className="w-40" data-testid={`docv-expiry-${r.id}`} />
                    ) : (r.expiry_date || "—")}
                    {r.ai_expiry && r.verification_status === "PENDING" && <div className="mt-1 text-[10px] text-slate-400">KI: {r.ai_expiry}</div>}
                  </td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_CLR[r.verification_status] || "bg-slate-100 text-slate-600"}`}>{r.verification_status}</span>
                    {r.review_note && <div className="mt-1 text-[10px] text-slate-400">{r.review_note}</div>}</td>
                  <td className="px-4 py-3 text-right">
                    {r.verification_status === "PENDING" && isAdmin ? (
                      <div className="flex justify-end gap-1">
                        <button disabled={busy === r.id} onClick={() => verify(r.id)} className="rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50" data-testid={`docv-verify-${r.id}`}>Bestätigen</button>
                        <button disabled={busy === r.id} onClick={() => reject(r.id)} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50" data-testid={`docv-reject-${r.id}`}>Ablehnen</button>
                      </div>
                    ) : r.verification_status === "VERIFIED" ? <CheckCircle2 className="ml-auto h-4 w-4 text-emerald-500" />
                      : r.verification_status === "REJECTED" ? <X className="ml-auto h-4 w-4 text-red-500" /> : null}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
